// src/components/AiDashboard/AiMyNutritionStatus/buildMyNutritionStatusLayout.js

import { MINIMUM_LOGGED_DAYS_FOR_TREND_ANALYSIS } from "../../nutrition/nutritionConfig";
import { hasEnoughLoggedDaysForTrendAnalysis } from "./helpers/insufficientDataHelpers";

function isFiniteNumber(value) {
  return typeof value === "number" && Number.isFinite(value);
}

function formatList(items) {
  const filtered = (items || []).filter(
    (value) => typeof value === "string" && value.trim()
  );
  if (filtered.length === 0) return "";
  if (filtered.length === 1) return filtered[0];
  if (filtered.length === 2) return `${filtered[0]} and ${filtered[1]}`;
  return `${filtered.slice(0, -1).join(", ")} and ${filtered[filtered.length - 1]
    }`;
}

function formatIsoDate(isoDateString) {
  if (!isoDateString || typeof isoDateString !== "string") return null;
  const safeString = `${isoDateString}T12:00:00Z`;
  const dateObject = new Date(safeString);
  if (Number.isNaN(dateObject.getTime())) return null;

  return dateObject.toLocaleDateString("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric"
  });
}


const NUTRIENT_METADATA = {
  energy_kcal: { displayName: "Energy" },
  energy_kj: { displayName: "Energy (kJ)" },
  protein_g: { displayName: "Protein" },
  fat_g: { displayName: "Fat" },
  carbohydrates_g: { displayName: "Carbohydrates", isPlural: true },
  fibre_g: { displayName: "Dietary fibre" },
  sugars_g: { displayName: "Sugars", isPlural: true },
  salt_g: { displayName: "Salt" },
  sodium_mg: { displayName: "Sodium" },
  saturatedFat_g: { displayName: "Saturated fat" },
  unsaturatedFat_g: { displayName: "Unsaturated fat" }
};


const NUTRIENT_FOOD_SOURCES = {
  energy_kcal: {
    genericFoods: ["nuts", "seeds", "oily fish", "avocado"],
    genericFoodsVariants: [
      ["nuts", "seeds", "oily fish", "avocado"],
      ["whole grains", "potatoes", "rice", "pasta"],
      ["nut butters", "cheese", "yogurt", "oils"]
    ]
  },
  protein_g: {
    genericFoods: ["beans", "lentils", "dairy products", "eggs", "fish"],
    genericFoodsVariants: [
      ["beans", "lentils", "dairy products", "eggs", "fish"],
      ["yogurt", "cheese", "lean meat", "tofu"],
      ["chicken", "turkey", "Greek yogurt", "cottage cheese"]
    ]
  },
  fat_g: {
    genericFoods: ["nuts", "seeds", "oils", "fatty fish"],
    genericFoodsVariants: [
      ["nuts", "seeds", "oils", "fatty fish"],
      ["olive oil", "avocado", "nut butters", "seeds"],
      ["rapeseed oil", "sunflower oil", "almonds", "salmon"]
    ]
  },
  carbohydrates_g: {
    genericFoods: ["whole grains", "potatoes", "rice", "pasta", "fruit"],
    genericFoodsVariants: [
      ["whole grains", "potatoes", "rice", "pasta", "fruit"],
      ["oats", "wholegrain bread", "quinoa", "fruit"],
      ["brown rice", "wholemeal pasta", "beans", "root vegetables"]
    ]
  },
  fibre_g: {
    genericFoods: ["vegetables", "whole grains", "legumes", "berries"],
    genericFoodsVariants: [
      ["vegetables", "whole grains", "legumes", "berries"],
      ["beans", "lentils", "peas", "wholegrain bread"],
      ["oats", "berries", "broccoli", "chickpeas"]
    ]
  },
  sugars_g: {
    genericFoods: ["sugary drinks", "sweets", "desserts", "fruit juices"],
    genericFoodsVariants: [
      ["sugary drinks", "sweets", "desserts", "fruit juices"],
      ["cakes", "chocolate", "ice cream", "pastries"],
      ["sweetened cereals", "sweetened yogurt", "jam", "sweet drinks"]
    ]
  },
  salt_g: {
    genericFoods: ["salty snacks", "processed meats", "ready meals", "sauces"],
    genericFoodsVariants: [
      ["salty snacks", "processed meats", "ready meals", "sauces"],
      ["crisps", "salted nuts", "instant noodles", "cured meats"],
      ["takeaway meals", "soy sauce", "ready-made soups", "cheese"]
    ]
  },
  sodium_mg: {
    genericFoods: ["salty snacks", "processed meats", "cheese", "ready meals"],
    genericFoodsVariants: [
      ["salty snacks", "processed meats", "cheese", "ready meals"],
      ["crisps", "salted nuts", "cured meats", "snack foods"],
      ["ready-made soups", "bread", "sauces", "processed cheeses"]
    ]
  },
  vitaminD_µg: {
    genericFoods: ["fatty fish", "fortified dairy drinks", "eggs"],
    genericFoodsVariants: [
      ["fatty fish", "fortified dairy drinks", "eggs"],
      ["salmon", "mackerel", "sardines", "fortified spreads"],
      ["fortified cereals", "egg yolks", "trout", "herring"]
    ]
  },
  vitaminC_mg: {
    genericFoods: ["citrus fruit", "berries", "kiwi", "peppers"],
    genericFoodsVariants: [
      ["citrus fruit", "berries", "kiwi", "peppers"],
      ["oranges", "strawberries", "blackcurrants", "broccoli"],
      ["kiwi", "bell peppers", "Brussels sprouts", "grapefruit"]
    ]
  },
  vitaminA_µg: {
    genericFoods: ["carrots", "dark green vegetables", "liver", "dairy"],
    genericFoodsVariants: [
      ["carrots", "dark green vegetables", "liver", "dairy"],
      ["sweet potatoes", "spinach", "kale", "egg yolks"],
      ["pumpkin", "butternut squash", "liver pâté", "fortified spreads"]
    ]
  },
  vitaminA_µg_RE: {
    genericFoods: ["carrots", "dark green vegetables", "liver", "dairy"],
    genericFoodsVariants: [
      ["carrots", "dark green vegetables", "liver", "dairy"],
      ["sweet potatoes", "spinach", "kale", "egg yolks"],
      ["pumpkin", "butternut squash", "liver pâté", "fortified spreads"]
    ]
  },
  folate_µg: {
    genericFoods: ["dark green vegetables", "beans", "lentils", "whole grains"],
    genericFoodsVariants: [
      ["dark green vegetables", "beans", "lentils", "whole grains"],
      ["spinach", "kale", "asparagus", "black beans"],
      ["chickpeas", "kidney beans", "lentils", "fortified breakfast cereals"]
    ]
  },
  vitaminB12_µg: {
    genericFoods: ["fish", "meat", "eggs", "dairy products"],
    genericFoodsVariants: [
      ["fish", "meat", "eggs", "dairy products"],
      ["beef", "liver", "cheese", "yogurt"],
      ["fish", "shellfish", "eggs", "fortified plant drinks"]
    ]
  },
  calcium_mg: {
    genericFoods: ["milk", "yogurt", "cheese", "fortified plant drinks"],
    genericFoodsVariants: [
      ["milk", "yogurt", "cheese", "fortified plant drinks"],
      ["milk", "cheese", "yogurt", "fortified plant drinks"],
      ["tofu set with calcium", "leafy greens", "almonds", "sesame seeds"]
    ]
  },
  iron_mg: {
    genericFoods: ["red meat", "beans", "lentils", "fortified cereals"],
    genericFoodsVariants: [
      ["red meat", "beans", "lentils", "fortified cereals"],
      ["beef", "lamb", "kidney beans", "chickpeas"],
      ["lentils", "spinach", "pumpkin seeds", "fortified breads"]
    ]
  },
  magnesium_mg: {
    genericFoods: ["nuts", "seeds", "whole grains", "legumes"],
    genericFoodsVariants: [
      ["nuts", "seeds", "whole grains", "legumes"],
      ["pumpkin seeds", "almonds", "cashews", "wholegrain bread"],
      ["spinach", "black beans", "oats", "peanut butter"]
    ]
  },
  iodine_µg: {
    genericFoods: ["iodized salt", "fish", "dairy products"],
    genericFoodsVariants: [
      ["iodized salt", "fish", "dairy products"],
      ["white fish", "cod", "milk", "yogurt"],
      ["seafood", "cheese", "eggs", "iodized salt"]
    ]
  },
  potassium_mg: {
    genericFoods: ["fruit", "vegetables", "potatoes", "beans"],
    genericFoodsVariants: [
      ["fruit", "vegetables", "potatoes", "beans"],
      ["bananas", "oranges", "tomatoes", "spinach"],
      ["potatoes", "beans", "lentils", "avocado"]
    ]
  },
  selenium_µg: {
    genericFoods: ["fish", "meat", "eggs", "Brazil nuts"],
    genericFoodsVariants: [
      ["fish", "meat", "eggs", "Brazil nuts"],
      ["Brazil nuts", "tuna", "pork", "eggs"],
      ["chicken", "cottage cheese", "whole grains", "sunflower seeds"]
    ]
  },
  saturatedFat_g: {
    genericFoods: ["fatty meats", "butter", "cream", "high-fat cheese"],
    genericFoodsVariants: [
      ["fatty meats", "butter", "cream", "high-fat cheese"],
      ["fatty cuts of meat", "sausages", "butter", "cream"],
      ["pastries", "cakes", "cheese", "ice cream"]
    ]
  },
  unsaturatedFat_g: {
    genericFoods: ["olive oil", "rapeseed oil", "nuts", "seeds", "fatty fish"],
    genericFoodsVariants: [
      ["olive oil", "rapeseed oil", "nuts", "seeds", "fatty fish"],
      ["olive oil", "rapeseed oil", "avocado", "walnuts"],
      ["sunflower oil", "pumpkin seeds", "flaxseeds", "mackerel"]
    ]
  }
};

/**
 * Strip unit / technical suffixes from keys like "vitaminA_µg_RE" → "vitaminA".
 */
function stripUnitSuffixes(rawKey) {
  if (!rawKey || typeof rawKey !== "string") return "";
  let base = rawKey;
  const unitPattern =
    /_(mg|µg|g|kj|kcal|NE|aTE|RE)$/i;

  // Remove multiple trailing suffix segments if present
  // e.g. vitaminA_µg_RE → vitaminA
  while (unitPattern.test(base)) {
    base = base.replace(unitPattern, "");
  }

  return base;
}

/**
 * Fallback: convert a raw key into a human-readable label.
 * Examples:
 *  - "fluoride_mg"        → "Fluoride"
 *  - "vitaminA_µg_RE"     → "Vitamin A"
 *  - "pantothenicAcid_mg" → "Pantothenic Acid"
 */
function humanizeNutrientKey(nutrientKey) {
  if (!nutrientKey || typeof nutrientKey !== "string") return "";

  let base = stripUnitSuffixes(nutrientKey);

  // Replace snake_case with spaces
  base = base.replace(/_/g, " ");

  // Insert spaces between camelCase boundaries
  base = base.replace(/([a-z])([A-Z])/g, "$1 $2");

  const words = base.split(/\s+/).filter(Boolean);
  if (!words.length) return nutrientKey;

  const titled = words.map((word) => {
    // Preserve all-caps segments like "B12"
    if (word.toUpperCase() === word) {
      return word;
    }

    if (word.length === 1 && /[a-z]/i.test(word)) {
      return word.toUpperCase();
    }

    return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
  });

  return titled.join(" ");
}

/**
 * Resolve displayName + plural info for a nutrient key.
 */
function resolveNutrientPresentation(nutrientKey) {
  const meta = NUTRIENT_METADATA[nutrientKey] || {};
  const displayName = meta.displayName || humanizeNutrientKey(nutrientKey);
  const isPlural = Boolean(meta.isPlural);
  return { displayName, isPlural };
}

/**
 * Classifies a percent-of-target value into "low" / "high" / "on_track".
 */
function classifyStatusFromPercent(percentOfTarget) {
  if (!isFiniteNumber(percentOfTarget)) return null;
  if (percentOfTarget < 80) return "low";
  if (percentOfTarget > 120) return "high";
  return "on_track";
}

/**
 * Build a compact date range sentence from rangeInfo + intakeSummary.
 */
function buildDateRangeSentence(rangeInfo, intakeSummary) {
  if (!rangeInfo || typeof rangeInfo !== "object") {
    return "Selected period (logged meals only).";
  }

  const { startDateIso, endDateIso, label } = rangeInfo;

  const formattedStart = startDateIso
    ? formatIsoDate(startDateIso)
    : null;
  const formattedEnd = endDateIso ? formatIsoDate(endDateIso) : null;

  const loggedDays = isFiniteNumber(
    intakeSummary && intakeSummary.totalDaysLogged
  )
    ? intakeSummary.totalDaysLogged
    : null;

  const loggedDaysText =
    loggedDays === null
      ? ""
      : ` (${loggedDays} logged ${loggedDays === 1 ? "day" : "days"
      }).`;

  if (formattedStart && formattedEnd && formattedStart !== formattedEnd) {
    return `${formattedStart} – ${formattedEnd}${loggedDaysText}`;
  }

  if (formattedStart) {
    return `${formattedStart}${loggedDaysText}`;
  }

  if (label && typeof label === "string" && label.trim()) {
    return `${label.trim()}${loggedDaysText}`;
  }

  return `Selected period${loggedDaysText}`;
}

/**
 * Extract and aggregate item-level information from rawDailyIntakeByDate.
 * This is used to:
 *  - Count how many items were actually logged (based on consumed[].id).
 *  - Work out which specific items drive particular nutrients up or down.
 *
 * It honours the Nutrition Calculate JSON shape:
 *   rawDailyIntakeByDate[dateIso].meals[*].meal.consumed[]
 * but also supports a flatter shape:
 *   rawDailyIntakeByDate[dateIso].meals[*].consumed[]
 */
function buildMealContext(rawDailyIntakeByDate) {
  // nutrientKey -> itemId -> { label, total }
  const nutrientContributionsByItem = {};
  const uniqueItemLabels = new Set();
  const uniqueMealNamesByDate = {};
  let totalLoggedItems = 0;

  if (
    !rawDailyIntakeByDate ||
    typeof rawDailyIntakeByDate !== "object"
  ) {
    return {
      nutrientMealDetailsByKey: {},
      uniqueMealNames: [],
      totalLoggedMeals: 0,
      uniqueMealNamesByDate: {}
    };
  }

  Object.entries(rawDailyIntakeByDate).forEach(
    ([dateIso, dailyDocument]) => {
      if (!dailyDocument || typeof dailyDocument !== "object") {
        return;
      }

      const meals = Array.isArray(dailyDocument.meals)
        ? dailyDocument.meals
        : [];

      if (!uniqueMealNamesByDate[dateIso]) {
        uniqueMealNamesByDate[dateIso] = new Set();
      }

      meals.forEach((mealEntry) => {
        // Support both shapes:
        //  - mealEntry.consumed
        //  - mealEntry.meal.consumed (Nutrition Calculate JSON)
        const consumedItems = Array.isArray(mealEntry.consumed)
          ? mealEntry.consumed
          : mealEntry.meal &&
            Array.isArray(mealEntry.meal.consumed)
            ? mealEntry.meal.consumed
            : [];

        consumedItems.forEach((consumedItem) => {
          if (!consumedItem || typeof consumedItem !== "object") {
            return;
          }

          const rawId =
            typeof consumedItem.id === "string" &&
              consumedItem.id.trim()
              ? consumedItem.id.trim()
              : null;

          const rawLabel =
            typeof consumedItem.label === "string" &&
              consumedItem.label.trim()
              ? consumedItem.label.trim()
              : typeof mealEntry.label === "string" &&
                mealEntry.label.trim()
                ? mealEntry.label.trim()
                : null;

          // We treat each id as the canonical "item" (what the AI already separated),
          // and fall back to the label if the id is missing.
          const itemId = rawId || rawLabel;
          if (!itemId) return;

          const itemLabel = rawLabel || rawId;

          uniqueItemLabels.add(itemLabel);
          uniqueMealNamesByDate[dateIso].add(itemLabel);
          totalLoggedItems += 1;

          const nutrients =
            consumedItem.nutrients ||
            consumedItem.nutrientTotals ||
            {};

          if (!nutrients || typeof nutrients !== "object") {
            return;
          }

          Object.entries(nutrients).forEach(
            ([nutrientKey, numericValue]) => {
              if (!isFiniteNumber(numericValue)) {
                return;
              }

              if (!nutrientContributionsByItem[nutrientKey]) {
                nutrientContributionsByItem[nutrientKey] = {};
              }

              const existing =
                nutrientContributionsByItem[nutrientKey][itemId] || {
                  label: itemLabel,
                  total: 0
                };

              existing.total += numericValue;
              nutrientContributionsByItem[nutrientKey][itemId] =
                existing;
            }
          );
        });
      });
    }
  );

  // Convert item-level aggregation into the structure used later
  // (topMeals = top contributing items for each nutrient).
  const nutrientMealDetailsByKey = {};
  Object.entries(nutrientContributionsByItem).forEach(
    ([nutrientKey, itemTotals]) => {
      const entriesArray = Object.values(itemTotals).sort(
        (left, right) => right.total - left.total
      );
      const total = entriesArray.reduce(
        (sum, entry) => sum + entry.total,
        0
      );

      const topMeals = entriesArray.slice(0, 3).map((entry) => ({
        mealName: entry.label,
        value: entry.total,
        share: total > 0 ? entry.total / total : 0
      }));

      nutrientMealDetailsByKey[nutrientKey] = {
        total,
        topMeals
      };
    }
  );

  return {
    nutrientMealDetailsByKey,
    uniqueMealNames: Array.from(uniqueItemLabels),
    // This is what the macro summary uses for
    // "you logged X meal items" – now based on consumed[].id.
    totalLoggedMeals: totalLoggedItems,
    uniqueMealNamesByDate
  };
}

/**
 * Deterministic "random" helper for wording variation.
 * Same seed → same choice; different seeds spread choices.
 */
function buildStableStringHash(source) {
  const text = String(source || "");
  let hash = 0;
  for (let index = 0; index < text.length; index += 1) {
    hash = (hash * 31 + text.charCodeAt(index)) | 0;
  }
  return Math.abs(hash);
}

function chooseTextVariant(variants, seedParts) {
  if (!Array.isArray(variants) || variants.length === 0) {
    return "";
  }
  const seedString = Array.isArray(seedParts)
    ? seedParts.join("|")
    : String(seedParts || "");
  const hash = buildStableStringHash(seedString);
  const index = hash % variants.length;
  return variants[index];
}

// Intro variants for recommendations
const LOW_INTRO_VARIANTS = [
  "Consider including more foods",
  "You may benefit from including more foods",
  "It may help to include more foods",
  "You could include more foods"
];

const HIGH_INTRO_VARIANTS = [
  "You can gently reduce high sources",
  "You may want to gently reduce high sources",
  "It may help to gently reduce high sources",
  "You could gradually reduce high sources"
];

const ON_TRACK_INTRO_VARIANTS = [
  "Keep including foods",
  "Continue including foods",
  "You can keep including foods",
  "Maintaining these foods is helpful"
];

/**
 * Templates for the "even your main sources ..." clause.
 *
 * We insert the meal names at the marker __MEALS__.
 * Semantics stay the same: those meals are the main sources but still not enough.
 */
const LOW_MAIN_SOURCE_TEMPLATES = [
  "even your main sources __MEALS__ only contributed small amounts",
  "even your main foods for this nutrient, __MEALS__, only provided modest amounts",
  "even the foods you rely on most, __MEALS__, only added relatively small amounts",
  "even your usual go-to sources __MEALS__ still left you below your overall target",
  "even your main contributors __MEALS__ were not enough to meet your overall target",
  "even your key sources for this nutrient, __MEALS__, only covered a small share of your needs",
  "even your most frequent sources __MEALS__ only made a limited contribution overall"
];

// Variants for the "main sources" clause for high / on-track
const HIGH_MAIN_SOURCE_VARIANTS = [
  ", mainly coming from",
  ", with most of the intake coming from",
  ", largely coming from",
  ", primarily coming from"
];

const ON_TRACK_MAIN_SOURCE_VARIANTS = [
  ", with regular contributions from",
  ", with steady contributions from",
  ", with frequent contributions from",
  ", with most days including"
];

/**
 * Per-report tracker to control low "main sources" wording:
 *  - First 7 uses: each template is used once (no repetition).
 *  - Later uses: deterministic pseudo-random over the same templates.
 */
class RecommendationVariationTracker {
  constructor() {
    this.lowMainSourceCount = 0;
  }

  pickLowMainSourceTemplate() {
    const variants = LOW_MAIN_SOURCE_TEMPLATES;
    if (!variants || variants.length === 0) {
      return null;
    }

    let index;

    if (this.lowMainSourceCount < variants.length) {
      // First N calls: walk the array in order for no duplicates.
      index = this.lowMainSourceCount;
    } else {
      // After that: deterministic pseudo-random based on count.
      const hash = buildStableStringHash(
        `low_main_sources|${this.lowMainSourceCount}`
      );
      index = hash % variants.length;
    }

    this.lowMainSourceCount += 1;
    return variants[index];
  }
}

/**
 * Build a short, food-based phrase like
 * "such as X and Y" using logged meals or generic fallbacks.
 *
 * For "high" nutrients we prioritise logged foods (to highlight drivers),
 * for "low" / "on_track" we prioritise generic foods (to reduce repetition).
 */
function buildFoodSourcePhrase(nutrientKey, mealContext, status) {
  const mealDetails =
    mealContext.nutrientMealDetailsByKey[nutrientKey];
  const foodsFromLogs = mealDetails
    ? mealDetails.topMeals.map((entry) => entry.mealName)
    : [];

  const foodSourceMeta = NUTRIENT_FOOD_SOURCES[nutrientKey];
  let fallbackFoods = [];

  if (foodSourceMeta) {
    if (Array.isArray(foodSourceMeta.genericFoodsVariants)) {
      const chosenVariant = chooseTextVariant(
        foodSourceMeta.genericFoodsVariants,
        [nutrientKey, "generic_foods"]
      );
      if (Array.isArray(chosenVariant)) {
        fallbackFoods = chosenVariant;
      }
    }
    if (!fallbackFoods.length && Array.isArray(foodSourceMeta.genericFoods)) {
      fallbackFoods = foodSourceMeta.genericFoods;
    }
  }

  const combined = [];

  const pushUnique = (label) => {
    if (
      typeof label === "string" &&
      label.trim() &&
      !combined.includes(label)
    ) {
      combined.push(label);
    }
  };

  if (status === "high") {
    foodsFromLogs.forEach(pushUnique);
    fallbackFoods.forEach(pushUnique);
  } else {
    fallbackFoods.forEach(pushUnique);
    foodsFromLogs.forEach(pushUnique);
  }

  if (!combined.length) return "";

  const phrase = formatList(combined.slice(0, 3));
  return `such as ${phrase}`;
}

/**
 * Build a one-sentence, food-based recommendation per nutrient,
 * using logged meals and generic food lists. This function does
 * not repeat the low/high/on-track classification; that is handled
 * in the baseline text shown next to it.
 */
function buildNutrientRecommendation(
  nutrientItem,
  mealContext,
  variationTracker
) {
  const { key, displayName, status } = nutrientItem;
  const prettyName = displayName || key || "This nutrient";
  const mealDetails =
    mealContext.nutrientMealDetailsByKey[key] || null;

  const foodPhrase = buildFoodSourcePhrase(
    key,
    mealContext,
    status
  );

  let mealContributionPhrase = "";

  if (mealDetails && mealDetails.topMeals.length > 0) {
    const topMealNames = mealDetails.topMeals
      .slice(0, 2)
      .map((entry) => entry.mealName);
    const mealsText = formatList(topMealNames);

    if (mealsText) {
      if (status === "low") {
        // Low: use templates with no repeats for the first 7 uses per report.
        const template =
          variationTracker &&
            typeof variationTracker.pickLowMainSourceTemplate === "function"
            ? variationTracker.pickLowMainSourceTemplate()
            : null;

        const renderedClause = template
          ? template.replace("__MEALS__", mealsText)
          : `even your main sources ${mealsText} only contributed small amounts`;

        mealContributionPhrase = `; ${renderedClause}`;
      } else if (status === "high") {
        const highContributionPrefix = chooseTextVariant(
          HIGH_MAIN_SOURCE_VARIANTS,
          [key, "high_main_sources"]
        );
        mealContributionPhrase = `${highContributionPrefix} ${mealsText}`;
      } else {
        const onTrackContributionPrefix = chooseTextVariant(
          ON_TRACK_MAIN_SOURCE_VARIANTS,
          [key, "on_track_main_sources"]
        );
        mealContributionPhrase = `${onTrackContributionPrefix} ${mealsText}`;
      }
    }
  }

  const isSalt = key === "salt_g" || key === "sodium_mg";

  // Special case: salt / sodium should never tell the user
  // to "eat more salt-rich foods" when low.
  // Keep this wording fixed for safety and clarity.
  if (isSalt) {
    if (status === "low") {
      return "Salt intake was below your reference level in this period. For most people this does not require increasing salt, unless you have specific medical guidance.";
    }

    if (status === "high") {
      const mealsText =
        mealDetails && mealDetails.topMeals.length > 0
          ? formatList(
            mealDetails.topMeals
              .slice(0, 2)
              .map((entry) => entry.mealName)
          )
          : "";
      const mainSourceText = mealsText
        ? `, mainly coming from ${mealsText}`
        : "";
      const saltFoodsPhrase = foodPhrase || "and processed meals";

      return `Salt-related intake (salt or sodium) was above your reference level${mainSourceText}. Try to limit salty foods ${saltFoodsPhrase}.`;
    }

    // on_track
    return "Salt intake was around your reference level. Keep following your current habits unless you have individual guidance from a clinician.";
  }

  // Generic nutrients (non-salt)

  if (status === "low") {
    const richClause = foodPhrase
      ? foodPhrase
      : `that are rich in ${prettyName}`;

    const introText = chooseTextVariant(
      LOW_INTRO_VARIANTS,
      [key, "low_intro"]
    );

    let recommendation = `${introText} ${richClause}`;
    if (mealContributionPhrase) {
      recommendation += mealContributionPhrase;
    }
    if (!recommendation.endsWith(".")) {
      recommendation += ".";
    }
    return recommendation;
  }

  if (status === "high") {
    const sourceClause = foodPhrase || `of ${prettyName}`;

    const introText = chooseTextVariant(
      HIGH_INTRO_VARIANTS,
      [key, "high_intro"]
    );

    let recommendation = `${introText} ${sourceClause}`;
    if (mealContributionPhrase) {
      recommendation += mealContributionPhrase;
    }
    if (!recommendation.endsWith(".")) {
      recommendation += ".";
    }
    return recommendation;
  }

  // on_track
  if (!foodPhrase && !mealContributionPhrase) {
    return "Keep following this pattern; it is close to your reference intake.";
  }

  const onTrackIntro = chooseTextVariant(
    ON_TRACK_INTRO_VARIANTS,
    [key, "on_track_intro"]
  );

  let recommendation = onTrackIntro;
  if (foodPhrase) {
    recommendation += ` ${foodPhrase}`;
  }
  if (mealContributionPhrase) {
    recommendation += mealContributionPhrase;
  }
  if (!recommendation.endsWith(".")) {
    recommendation += ".";
  }
  return recommendation;
}

/**
 * Build macro-level summary paragraph (energy + key macros +
 * lowest/highest nutrients + which meals drove highs).
 */
function buildMacroSummary({
  intakeSummary,
  comparisonVsTargets,
  rangeInfo,
  nutrientItemsByStatus,
  mealContext
}) {
  if (
    !comparisonVsTargets ||
    typeof comparisonVsTargets !== "object"
  ) {
    return "This period did not include comparison to nutrient targets, so only basic totals are available.";
  }

  const macrosOfInterest = [
    "energy_kcal",
    "protein_g",
    "fat_g",
    "carbohydrates_g",
    "fibre_g",
    "salt_g",
    "sodium_mg"
  ];

  const macroStatusByKey = {};
  macrosOfInterest.forEach((macroKey) => {
    const entry = comparisonVsTargets[macroKey];
    if (!entry || typeof entry !== "object") return;
    const percent =
      entry.percentOfTarget ?? entry.percent_of_target;
    if (!isFiniteNumber(percent)) return;

    const status = classifyStatusFromPercent(percent);
    if (!status) return;

    macroStatusByKey[macroKey] = {
      status,
      percentOfTarget: percent
    };
  });

  const sentences = [];

  // Energy sentence
  const energyEntry = macroStatusByKey.energy_kcal;
  if (energyEntry) {
    sentences.push(
      `Your logged meals for this period provided about ${Math.round(
        energyEntry.percentOfTarget
      )}% of your daily energy target.`
    );
  } else {
    sentences.push(
      "Your logged meals for this period were compared with your daily energy and macronutrient targets."
    );
  }

  // Protein / fat / carbohydrates grouping
  const macroLabels = {
    protein_g: "protein",
    fat_g: "fat",
    carbohydrates_g: "carbohydrates"
  };
  const lowMacros = [];
  const highMacros = [];
  const onTrackMacros = [];

  Object.entries(macroLabels).forEach(([key, label]) => {
    const macroEntry = macroStatusByKey[key];
    if (!macroEntry) return;
    if (macroEntry.status === "low") {
      lowMacros.push(label);
    } else if (macroEntry.status === "high") {
      highMacros.push(label);
    } else {
      onTrackMacros.push(label);
    }
  });

  const macroPhrases = [];
  if (lowMacros.length) {
    macroPhrases.push(
      `${formatList(lowMacros)} were below target`
    );
  }
  if (onTrackMacros.length) {
    macroPhrases.push(
      `${formatList(onTrackMacros)} were roughly on track`
    );
  }
  if (highMacros.length) {
    macroPhrases.push(
      `${formatList(highMacros)} were above target`
    );
  }

  if (macroPhrases.length) {
    const combinedMacroPhrase =
      macroPhrases.length === 1
        ? macroPhrases[0]
        : `${macroPhrases
          .slice(0, -1)
          .join(", ")} and ${macroPhrases[macroPhrases.length - 1]
        }`;
    sentences.push(
      `In terms of macronutrients, ${combinedMacroPhrase} for this period.`
    );
  }

  // Fibre and salt specifics
  const fibreEntry = macroStatusByKey.fibre_g;
  if (fibreEntry) {
    if (fibreEntry.status === "low") {
      sentences.push(
        "Dietary fibre was low, suggesting that more vegetables, whole grains, legumes and fruit could help."
      );
    } else if (fibreEntry.status === "high") {
      sentences.push(
        "Dietary fibre was above your target, which is usually positive as long as it feels comfortable for you."
      );
    } else {
      sentences.push(
        "Dietary fibre was roughly on track for this period."
      );
    }
  }

  const saltEntry =
    macroStatusByKey.salt_g || macroStatusByKey.sodium_mg;
  if (saltEntry) {
    if (saltEntry.status === "high") {
      sentences.push(
        "Salt-related intake (salt or sodium) was high, so moderating salty foods and processed meals may be helpful."
      );
    } else if (saltEntry.status === "low") {
      sentences.push(
        "Salt-related intake (salt or sodium) was below target, which is usually safe as long as you feel well and hydrated."
      );
    } else {
      sentences.push(
        "Salt-related intake (salt or sodium) was around your reference level."
      );
    }
  }

  // Lowest and highest nutrients overall
  const lowestNutrients = (nutrientItemsByStatus.low || [])
    .slice()
    .sort(
      (left, right) =>
        (left.percentOfTarget ?? 9999) -
        (right.percentOfTarget ?? 9999)
    )
    .slice(0, 4);
  const highestNutrients = (nutrientItemsByStatus.high || [])
    .slice()
    .sort(
      (left, right) =>
        (right.percentOfTarget ?? 0) -
        (left.percentOfTarget ?? 0)
    )
    .slice(0, 4);

  if (lowestNutrients.length) {
    sentences.push(
      `The nutrients furthest below target were ${formatList(
        lowestNutrients.map(
          (nutrient) => nutrient.displayName || nutrient.name
        )
      )}.`
    );
  }

  if (highestNutrients.length) {
    sentences.push(
      `Nutrients that stood out as high were ${formatList(
        highestNutrients.map(
          (nutrient) => nutrient.displayName || nutrient.name
        )
      )}.`
    );
  }

  // Logged days and meals
  const loggedDays = isFiniteNumber(
    intakeSummary && intakeSummary.totalDaysLogged
  )
    ? intakeSummary.totalDaysLogged
    : null;
  const daysInRange = isFiniteNumber(
    rangeInfo && rangeInfo.daysInRange
  )
    ? rangeInfo.daysInRange
    : null;

  if (loggedDays !== null && daysInRange !== null) {
    sentences.push(
      `You logged meals on ${loggedDays} out of ${daysInRange} days in this selected period.`
    );
  } else if (loggedDays !== null) {
    sentences.push(
      `You logged meals on ${loggedDays} ${loggedDays === 1 ? "day" : "days"
      } in this period.`
    );
  }

  if (mealContext.totalLoggedMeals > 0) {
    const exampleMeals = mealContext.uniqueMealNames.slice(0, 3);
    const exampleMealsText = formatList(exampleMeals);

    if (exampleMealsText) {
      sentences.push(
        `Across the period, you logged ${mealContext.totalLoggedMeals} meal items, including examples like ${exampleMealsText}.`
      );
    } else {
      sentences.push(
        `Across the period, you logged ${mealContext.totalLoggedMeals} meal items.`
      );
    }
  }

  // High nutrients + meals that dominate them
  if (highestNutrients.length && mealContext.totalLoggedMeals > 0) {
    const highNutrientMealSnippets = [];
    highestNutrients.forEach((nutrient) => {
      const details =
        mealContext.nutrientMealDetailsByKey[nutrient.key];
      if (!details || !details.topMeals.length) return;
      const mealNames = details.topMeals
        .slice(0, 2)
        .map((entry) => entry.mealName);
      const mealsText = formatList(mealNames);
      if (!mealsText) return;
      highNutrientMealSnippets.push(
        `${nutrient.displayName || nutrient.name} from ${mealsText}`
      );
    });

    if (highNutrientMealSnippets.length) {
      sentences.push(
        `For nutrients that were high, most of the intake came from a few logged meals, for example ${formatList(
          highNutrientMealSnippets
        )}.`
      );
    }
  }

  return sentences.join(" ");
}

/**
 * Text shown when there are fewer than 10 logged days.
 */
const LIMITED_DATA_SUMMARY_TEXT =
  "To truly understand your nutrition habits, you need more than just a few days of data. " +
  "Short-term logs can show quick snapshots, but they often miss the bigger picture. " +
  "By selecting 10 days or more, you'll start to see meaningful trends.";

/**
 * Build a layout for cases where there are not enough logged days
 * for meaningful trend analysis.
 */
function buildLimitedDataLayout(rangeInfo, intakeSummary) {
  const rangeSentence = buildDateRangeSentence(rangeInfo, intakeSummary);
  const disclaimer = buildDisclaimerText(rangeSentence, intakeSummary);

  return {
    title: "My Logged Nutrition Status",
    dateRange: rangeSentence,
    macroSummary: LIMITED_DATA_SUMMARY_TEXT,
    lowNutrients: [],
    highNutrients: [],
    onTrackNutrients: [],
    disclaimer
  };
}

/**
 * Build disclaimer / "how to use this" block for the PDF.
 */
function buildDisclaimerText(rangeSentence, intakeSummary) {
  const loggedDays = isFiniteNumber(
    intakeSummary && intakeSummary.totalDaysLogged
  )
    ? intakeSummary.totalDaysLogged
    : null;

  const howToUseLines = [];

  howToUseLines.push(
    "How to use this report: Before eating on any given day, open the Suggested Consumption Food Category dropdown. Select a food category to view personalized recommendations. You will also see the Macros and Vitamins & Minerals for that day, showing which nutrients are already high or low."
  );
  howToUseLines.push(
    'When you add a meal, open the "Item Nutrition" view to see how that specific item contributes to your totals.'
  );

  // Only call out "relatively few logged days" when the count is genuinely small.
  if (loggedDays !== null && loggedDays < 5) {
    howToUseLines.push(
      "Because there are relatively few logged days in this range, treat this report as a rough guide rather than a complete picture."
    );
  }

  howToUseLines.push(
    "It is very important that you log your meals in accordance with their ingredients to the best of your knowledge. This ensures the most accurate results. If some days in the selected period have no logged meals, the results may not reflect your full intake."
  );

  const medicalLines = [];
  medicalLines.push(
    "This report is based on your self‑reported meals together with your profile settings (such as age, sex, height, weight, activity level, and dietary preferences). These inputs are used to calculate personalized nutrition ranges and recommendations, aligned with the Nordic Nutrition Recommendations 2023 (NNR2023), including recommended intake values (RI), adequate intake values (AI), upper levels (UL), and other nutrient reference ranges."
  );
  medicalLines.push(
    "This information is provided for informational purposes only and does not constitute medical advice, diagnosis, or treatment. Pure Health Nexus is not affiliated with, endorsed by, or assisted in any way by the Nordic Nutrition Recommendations 2023 (NNR2023), its authors, editors, or governing bodies."
  );
  medicalLines.push(
    "If you have health conditions, are pregnant, or have special dietary needs, discuss these patterns with a qualified healthcare professional before making major changes."
  );

  return `${rangeSentence} ${howToUseLines.join(
    " "
  )} ${medicalLines.join(" ")}`;
}

/**
 * Main narrative builder class.
 */
class MyNutritionStatusNarrativeBuilder {
  constructor(nutritionTargetsCalculations) {
    this.snapshot = nutritionTargetsCalculations || {};
    this.intakeSummary = this.snapshot.intakeSummary || {};
    this.comparisonVsTargets =
      this.intakeSummary.comparisonVsTargets || {};
    this.rangeInfo = this.snapshot.rangeInfo || {};
    this.rawDailyIntakeByDate =
      this.snapshot.rawDailyIntakeByDate || {};

    this.mealContext = buildMealContext(
      this.rawDailyIntakeByDate
    );

    // Per-report variation tracker so phrases can vary without losing determinism.
    this.variationTracker = new RecommendationVariationTracker();
  }

  buildNutrientCollections() {
    const allNutrients = [];
    const byStatus = {
      low: [],
      high: [],
      on_track: []
    };

    if (
      !this.comparisonVsTargets ||
      typeof this.comparisonVsTargets !== "object"
    ) {
      return { allNutrients, byStatus };
    }

    const nutrientKeys = Object.keys(
      this.comparisonVsTargets
    ).sort();

    const hasEnergyKcal =
      typeof this.comparisonVsTargets.energy_kcal === "object";

    nutrientKeys.forEach((nutrientKey) => {
      // Avoid duplicating energy in two units; prefer kcal if present.
      if (nutrientKey === "energy_kj" && hasEnergyKcal) {
        return;
      }

      const entry = this.comparisonVsTargets[nutrientKey];
      if (!entry || typeof entry !== "object") return;

      const percent =
        entry.percentOfTarget ?? entry.percent_of_target;
      if (!isFiniteNumber(percent)) return;

      const status = classifyStatusFromPercent(percent);
      if (!status) return;

      const { displayName, isPlural } =
        resolveNutrientPresentation(nutrientKey);

      const nutrientItem = {
        key: nutrientKey,
        name: displayName,
        displayName,
        percentOfTarget: percent,
        status,
        isPlural,
        recommendation: "", // to be filled
        summary: "" // kept for compatibility with existing consumers
      };

      allNutrients.push(nutrientItem);
      byStatus[status].push(nutrientItem);
    });

    return { allNutrients, byStatus };
  }

  applyRecommendations(allNutrients) {
    return allNutrients.map((nutrientItem) => {
      const recommendation = buildNutrientRecommendation(
        nutrientItem,
        this.mealContext,
        this.variationTracker
      );
      return {
        ...nutrientItem,
        recommendation,
        summary: recommendation
      };
    });
  }

  buildLayout() {
    const hasComparison =
      this.comparisonVsTargets &&
      typeof this.comparisonVsTargets === "object" &&
      Object.keys(this.comparisonVsTargets).length > 0;

    // Case 1: No comparison data → keep existing "no comparison" behaviour
    if (!hasComparison) {
      const rangeSentence = buildDateRangeSentence(
        this.rangeInfo,
        this.intakeSummary
      );
      const disclaimer = buildDisclaimerText(
        rangeSentence,
        this.intakeSummary
      );
      return {
        title: "My Logged Nutrition Status",
        dateRange: rangeSentence,
        macroSummary:
          "There is not enough target comparison data for this period to summarise your nutrient balance.",
        lowNutrients: [],
        highNutrients: [],
        onTrackNutrients: [],
        disclaimer
      };
    }

    // Case 2: Comparison exists, but too few logged days → limited summary only
    const hasSufficientLoggedDays = hasEnoughLoggedDaysForTrendAnalysis(
      this.snapshot,
      MINIMUM_LOGGED_DAYS_FOR_TREND_ANALYSIS
    );

    if (!hasSufficientLoggedDays) {
      return buildLimitedDataLayout(this.rangeInfo, this.intakeSummary);
    }

    // Case 3: Comparison exists and enough logged days → full existing behaviour

    const { allNutrients } =
      this.buildNutrientCollections();
    const enrichedNutrients =
      this.applyRecommendations(allNutrients);

    const enrichedByStatus = {
      low: [],
      high: [],
      on_track: []
    };

    enrichedNutrients.forEach((item) => {
      if (item.status === "low") {
        enrichedByStatus.low.push(item);
      } else if (item.status === "high") {
        enrichedByStatus.high.push(item);
      } else {
        enrichedByStatus.on_track.push(item);
      }
    });

    const rangeSentence = buildDateRangeSentence(
      this.rangeInfo,
      this.intakeSummary
    );

    const macroSummary = buildMacroSummary({
      intakeSummary: this.intakeSummary,
      comparisonVsTargets: this.comparisonVsTargets,
      rangeInfo: this.rangeInfo,
      nutrientItemsByStatus: enrichedByStatus,
      mealContext: this.mealContext
    });

    const disclaimer = buildDisclaimerText(
      rangeSentence,
      this.intakeSummary
    );

    return {
      title: "My Logged Nutrition Status",
      dateRange: rangeSentence,
      macroSummary,
      lowNutrients: enrichedByStatus.low,
      highNutrients: enrichedByStatus.high,
      onTrackNutrients: enrichedByStatus.on_track,
      disclaimer
    };
  }
}

export default function buildMyNutritionStatusLayout(
  nutritionTargetsCalculations
) {
  const builder = new MyNutritionStatusNarrativeBuilder(
    nutritionTargetsCalculations
  );
  return builder.buildLayout();
}
